import React from "react";
import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { Button, TextInput } from "@narmi/design_system";
import {
  ContextForm,
  useFormData,
  EmailFormField,
  CsrfTokenMiddleware,
  jsonFromDocument,
} from "cerulean";
import "./authentication.css";

const element = document.getElementById("password_reset_done");
const passwordResetSubmitted = element ? element.textContent === "true" : false;
const passwordResetIdentifier = jsonFromDocument(
  "password_reset_identifier"
) as "email" | "username";

const PasswordReset = () => {
  const { l10n } = useLocalization();
  const { formData, onChange } = useFormData();
  const form = React.useRef<HTMLFormElement>(null);

  const isUsernameReset = passwordResetIdentifier === "username";
  const { headerText, helpText } = (() => {
    if (passwordResetSubmitted) {
      return {
        headerText: l10n.getString("heading-email-sent"),
        helpText: l10n.getString("subheading-sent-reset-instructions"),
      };
    }

    if (isUsernameReset) {
      return {
        headerText: l10n.getString("heading-forgot-password"),
        helpText: l10n.getString("subheading-enter-username"),
      };
    }

    return {
      headerText: l10n.getString("heading-forgot-username"),
      helpText: l10n.getString("subheading-recover-username"),
    };
  })();

  return (
    <div className="authentication-container">
      <div
        className={cc([{ "mobile-web-extra-space": !passwordResetSubmitted }])}
      >
        <h1>{headerText}</h1>
        <div data-test-element="password-confirm-text">{helpText}</div>
      </div>
      {passwordResetSubmitted ? (
        <ContextForm.ActionBar>
          <Button
            label={l10n.getString("label-back-to-login")}
            onClick={() => window.location.assign("/login")}
            data-test-element="back-to-login-button"
            testId="back-to-login"
          />
        </ContextForm.ActionBar>
      ) : (
        <ContextForm
          ref={form}
          data={formData}
          onChange={onChange}
          autoComplete="off"
          method="post"
        >
          {isUsernameReset ? (
            <ContextForm.Field required>
              <TextInput
                name="username"
                field="username"
                label={l10n.getString("label-username")}
              />
            </ContextForm.Field>
          ) : (
            <EmailFormField required />
          )}
          <CsrfTokenMiddleware />
          <ContextForm.ActionBar>
            <ContextForm.Action
              onSubmit={(_data: unknown, e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                form.current?.submit();
              }}
            >
              <Button
                label={l10n.getString("button-submit")}
                testId="submit-password-reset"
                data-test-element="submit-button"
              />
            </ContextForm.Action>
            {isUsernameReset && (
              <a href="/password_reset/email">
                {l10n.getString("link-forgot-username")}
              </a>
            )}
          </ContextForm.ActionBar>
        </ContextForm>
      )}
    </div>
  );
};

export default PasswordReset;
